import React, { useId } from 'react';
import { HatGlyph } from './HatIcon';
import PlanetSurface, { SurfaceDefs, HAT_SURFACE, HAT_TILT } from './PlanetSurface';
import { SphereDefs, SphereAtmosphere, SphereGlass } from './Sphere';

/*
 * A citizen of one culture, standing on their own world.
 *
 * One scene serves all nine. The parts that change are read from the canon
 * rather than hand-drawn per culture: `diurnal` picks the sky and decides
 * whether the ground carries settlement lights, `body.glow` sets how much light
 * the world makes for itself, `body.rings` and `body.moons` are drawn as the
 * orrery draws them. So a portrait cannot drift from the planet it depicts --
 * change the data and both follow.
 *
 * What the citizen carries is the culture's own mark, on a standard. Not a
 * per-culture implement: a lantern and an anemometer would have been a second
 * visual vocabulary for the same nine things, and a reader would have had to
 * learn that the figure with the weather vane is the planet with the loop on
 * it. The same HatGlyph rides the planet, the chip and the standard, with the
 * same halo treatment it uses in the orrery, so it stays legible against an
 * accent of any lightness.
 */

const VB_W = 176;
const VB_H = 132;
const WIDE_VB_W = VB_W * 3;
const GROUND = 104; // where the citizen's feet meet the horizon
const FIG_X = 44;
const FIG_SCALE = 1.28; // the first pass left the citizens small in a lot of empty sky

/*
 * Nine body plans.
 *
 * The first pass drew one humanoid nine times, which quietly said the cultures
 * differ only in what they carry. They do not. A culture that lives in cloud
 * tops watching other people move has no reason to share a skeleton with one
 * that is mostly carapace, and the forgetenders are not people at all -- they
 * are a bounded fire, which is the whole argument of that discipline.
 *
 * Authored feet-at-origin, y negative upward, ~36 tall, and read as silhouette
 * first: these sit at roughly 40px on a card.
 *
 * COLOUR. Not one black nine times. Which way a culture goes is decided by the
 * sky it stands in, not by preference: day scenes have bright skies, so those
 * peoples are dark and tinted toward their own world; night scenes are dark, so
 * those peoples are pale and catch what light there is. Every ink below clears
 * 3:1 against both its own ground and its own sky -- a black warden on a night
 * sky was under 2:1 and survived only because the shape was simple.
 *
 * `markInk` flips with it: accent marks read on a dark body, but on a pale one
 * the eyes have to go dark or they vanish into the head.
 *   parts  filled shapes, or stroked where `line`
 *   mark   accent-coloured details -- eyes, cores, facets. The only colour.
 *   reach  a limb to the flag, for the ones that have limbs
 *   hover  floats, and is given a contact shadow instead of feet
 */
export const CITIZEN_FORM = {
  // Wayfinders: buoyant, trailing, and mostly eye. They watch for a living.
  ux: {
    ink: '#1c1222',
    hover: true,
    parts: [
      { d: 'M 0 -38 C 8 -36 9 -25 5.5 -17 C 3.5 -11 1.5 -8 0 -4 C -1.5 -8 -3.5 -11 -5.5 -17 C -9 -25 -8 -36 0 -38 Z' },
      { d: 'M -3 -12 L -4.5 -3', line: true },
      { d: 'M 3 -12 L 4.5 -3', line: true },
    ],
    // A lens rather than a disc, or it reads as a dot instead of an eye.
    mark: [{ d: 'M -5.4 -29.5 Q 0 -34 5.4 -29.5 Q 0 -25 -5.4 -29.5 Z' }],
    reach: 'M 4 -26 L 11 -29',
  },

  /*
   * Patternkeepers: a cluster, not a blade. One tall shard read as a dagger --
   * three of different heights read as something grown, and give the vein
   * somewhere to run across.
   */
  design: {
    ink: '#1e1733',
    parts: [
      { d: 'M 0 -38 L 5 -26 L 3.5 -6 L 0 -2 L -3.5 -6 L -5 -26 Z' },
      { d: 'M -7.5 -27 L -4.5 -19 L -5.5 -4 L -8.5 -2 L -10 -17 Z' },
      { d: 'M 7 -22 L 9.5 -15 L 8 -3 L 5 -4 L 4.5 -16 Z' },
    ],
    // The one vein, carried through all three. Which is the whole creed.
    mark: [{ d: 'M -9 -13 L 8.5 -10', line: true }],
    reach: 'M 5 -22 L 11 -26',
  },

  /*
   * Stratigraphers: terraces, offset. Centred slabs stacked into a bin; the
   * offsets make it a cut cliff face, which is what they actually live on.
   */
  arch: {
    ink: '#1b1e23',
    inkAlt: '#3a3e47',
    parts: [
      { d: 'M -9 -14 L 7 -14 L 7 -6.5 L -9 -6.5 Z' },
      { d: 'M -6 -21.5 L 9.5 -21.5 L 9.5 -14.5 L -6 -14.5 Z', alt: true },
      { d: 'M -8.5 -29 L 5 -29 L 5 -22 L -8.5 -22 Z' },
      { d: 'M -4.5 -35.5 L 6.5 -35.5 L 6.5 -29.5 L -4.5 -29.5 Z', alt: true },
      { d: 'M -6 -6.5 L -3 -6.5 L -3 0 L -6 0 Z' },
      { d: 'M 2 -6.5 L 5 -6.5 L 5 0 L 2 0 Z' },
    ],
    mark: [{ d: 'M -2.5 -33 L 4.5 -33', line: true }],
    reach: 'M 7 -25 L 11 -27',
  },

  /*
   * Forgetenders: an open frame with the fire visible between the ribs. The
   * first pass caged the flame inside the dark silhouette, which produced a
   * bucket -- the light has to be the thing you see, not the container.
   */
  ai: {
    ink: '#1a110d',
    hover: true,
    parts: [
      { d: 'M -8.5 -3 L 8.5 -3 L 6.5 -10 L -6.5 -10 Z' },
      { d: 'M -6.5 -10 C -12 -21 -7.5 -31 -2.5 -34', line: true },
      { d: 'M 6.5 -10 C 12 -21 7.5 -31 2.5 -34', line: true },
      { d: 'M -2.5 -34 L 2.5 -34', line: true },
    ],
    mark: [{ d: 'M 0 -31 C 6 -23 4.5 -15 0 -11 C -4.5 -15 -6 -23 0 -31 Z', glow: true }],
  },

  // The Deep Current: a mantle and many working limbs. Hands in the codebase.
  dev: {
    ink: '#8fa8c0',
    markInk: '#0e1a2b',
    parts: [
      { d: 'M 0 -36 C 8.5 -34 9.5 -24 6.5 -17 L -6.5 -17 C -9.5 -24 -8.5 -34 0 -36 Z' },
      { d: 'M -6 -17 C -8 -11 -9 -6 -10 -1', line: true },
      { d: 'M -2.5 -17 C -3.5 -10 -3.5 -5 -4 0', line: true },
      { d: 'M 2.5 -17 C 3.5 -10 3.5 -5 4 0', line: true },
      { d: 'M 6 -17 C 8 -12 9 -7 10.5 -2', line: true },
    ],
    mark: [
      { d: 'M -3.6 -29 A 1.7 1.7 0 1 0 -0.2 -29 A 1.7 1.7 0 1 0 -3.6 -29 Z' },
      { d: 'M 0.2 -29 A 1.7 1.7 0 1 0 3.6 -29 A 1.7 1.7 0 1 0 0.2 -29 Z' },
    ],
  },

  // Gatekeepers: planted, low, and wider at the base than the top. Immovable.
  test: {
    ink: '#dcc79c',
    markInk: '#4a3a1e',
    parts: [
      { d: 'M -10 -3 L 10 -3 L 8 -17 C 8 -24 -8 -24 -8 -17 Z' },
      { d: 'M -11 -3 L 11 -3 L 11 0 L -11 0 Z' },
    ],
    mark: [{ d: 'M -5 -18 L 5 -18', line: true }],
    reach: 'M 7 -16 L 11 -20',
  },

  /*
   * Stormwrights: a pinwheel. Three thin strokes off a hub disappeared at this
   * size, so the blades are filled and broad -- the rotor has to be the
   * silhouette, not a detail hung on a post.
   */
  devops: {
    ink: '#9fadb7',
    inkAlt: '#5d6a74',
    markInk: '#2b3a31',
    parts: [
      { d: 'M -3.5 0 L 3.5 0 L 2.5 -19 L -2.5 -19 Z', alt: true },
      { d: 'M 0 -26 C 4 -33 2.5 -40 -2 -41 C -3 -35 -2 -29 0 -26 Z', spin: 0 },
      { d: 'M 0 -26 C 4 -33 2.5 -40 -2 -41 C -3 -35 -2 -29 0 -26 Z', spin: 120 },
      { d: 'M 0 -26 C 4 -33 2.5 -40 -2 -41 C -3 -35 -2 -29 0 -26 Z', spin: 240 },
      { d: 'M 0 -30.5 A 4.5 4.5 0 1 0 0 -21.5 A 4.5 4.5 0 1 0 0 -30.5 Z' },
    ],
    mark: [{ d: 'M -1.7 -26 A 1.7 1.7 0 1 0 1.7 -26 A 1.7 1.7 0 1 0 -1.7 -26 Z' }],
    reach: 'M 3 -16 L 11 -20',
  },

  /*
   * Wardens: an angular carapace. A smooth dome read as a mound; facets and
   * plate seams read as armour, which is the point of the culture.
   */
  sec: {
    ink: '#b08a91',
    inkAlt: '#6d4e55',
    markInk: '#3a2328',
    parts: [
      { d: 'M -11.5 -10 L -8.5 -21 L -3.5 -27 L 3.5 -27 L 8.5 -21 L 11.5 -10 Z' },
      { d: 'M -8 -10 L 8 -10 L 7 -3 L -7 -3 Z', alt: true },
      { d: 'M -2.6 -30.6 A 2.6 2.6 0 1 0 2.6 -30.6 A 2.6 2.6 0 1 0 -2.6 -30.6 Z' },
      { d: 'M -6 -3 L -3 -3 L -3 0 L -6 0 Z' },
      { d: 'M 3 -3 L 6 -3 L 6 0 L 3 0 Z' },
    ],
    mark: [
      { d: 'M -8.6 -17 L 8.6 -17', line: true },
      { d: 'M -5.4 -23 L 5.4 -23', line: true },
    ],
    reach: 'M 8 -12 L 11 -16',
  },

  // Nightwatch: tall, thin, and all of it neck and eyes. Built to keep looking.
  sre: {
    ink: '#cfe6e3',
    markInk: '#1b3a38',
    parts: [
      { d: 'M -4 0 L 4 0 L 3 -24 L -3 -24 Z' },
      { d: 'M -1.4 -24 L 1.4 -24 L 1.4 -33 L -1.4 -33 Z' },
      { d: 'M 0 -41 A 4.2 4.2 0 1 0 0 -32.6 A 4.2 4.2 0 1 0 0 -41 Z' },
    ],
    mark: [
      { d: 'M -2.4 -38 A 1.1 1.1 0 1 0 -0.2 -38 A 1.1 1.1 0 1 0 -2.4 -38 Z' },
      { d: 'M 0.6 -38 A 1.1 1.1 0 1 0 2.8 -38 A 1.1 1.1 0 1 0 0.6 -38 Z' },
      { d: 'M -1 -34.6 A 1 1 0 1 0 1 -34.6 A 1 1 0 1 0 -1 -34.6 Z' },
    ],
    reach: 'M 3.4 -20 L 11 -24',
  },
};


/*
 * What each culture flies.
 *
 * Two families, chosen by the sky rather than by taste: day cultures live in an
 * atmosphere and fly things that need one -- balloon, glider, airship. Night
 * cultures are out between worlds on long orbits, so theirs are spacecraft.
 * The forgetenders sit on the boundary and get a lifter that carries its own
 * fire, which is the only way that culture ever travels.
 *
 * Authored in a ~26x20 box centred on the origin, in the same silhouette budget
 * as the citizens, and inked from the same --cz-ink: one people, one material.
 *
 * ORIENTATION. Every craft faces +x with its top at -y, without exception. A
 * vehicle needs a front and a back or it reads as an ornament, and the airship
 * proved the cost of not fixing this early -- its fins were drawn aft of a
 * symmetrical hull, which is correct geometry for a machine flying backwards.
 * So: nose, prow or envelope forward; fins, nozzles and panels aft; sails,
 * hardpoints and canopies up; gondolas, lamps and landing gear down.
 *
 * JOINTS. Every appendage roots INSIDE the hull rather than meeting its edge.
 * A fin whose base lands exactly on the outline leaves a hairline of sky at
 * some zoom levels and on some renderers; overlapping by a unit or two costs
 * nothing, because both shapes carry the same ink.
 *
 * `seams` are the plating: thin strokes tinted away from the hull, which is
 * what keeps a craft from reading as a flat cut-out. `lights` blink on their
 * own offsets -- warm ones are lit windows and engines, accent ones are
 * navigation and sensors.
 *
 * `badge` is the culture's own mark, painted on the hull the way a squadron
 * marking is: no roundel behind it, because a disc plus a halo plus a glyph is
 * three layers in about ten pixels and turns to mush. It takes `--cz-mark-ink`
 * instead, which is already the tone chosen to read against that hull -- so
 * the same mark rides the planet, the flag and the ship.
 */
export const CRAFT = {
  /*
   * Wayfinders: a powered balloon. Rebuilt as an envelope that narrows to a
   * mouth, a load ring, rigging, and a car -- the first version hung a box
   * under a teardrop with a gap between them and no reason for either.
   */
  ux: {
    badge: { x: 0.6, y: -5, size: 8 },
    parts: [
      { d: 'M 1 -14 C 9.6 -13.6 12.6 -6 9.6 0.4 C 7.6 4.6 4.6 7.4 3 9.6 L -1.6 9.6 C -3.4 7 -6.8 4 -8.8 -0.6 C -11.6 -7 -7.6 -14 1 -14 Z' },
      // A steering fin aft: this one is powered, and needs a heading.
      { d: 'M -8.2 -2.6 C -11.6 -4 -14.4 -5.2 -15 -4.6 C -14.4 -2.4 -14.2 0 -14.8 1.8 C -14 2.2 -11.2 1 -8.2 0.2 Z', alt: true },
      // The mouth and load ring the rigging actually hangs from.
      { d: 'M -1.9 9.2 L 3.3 9.2 L 2.9 10.9 L -1.5 10.9 Z', alt: true },
      { d: 'M -1 10.7 L -0.6 13.1', line: true },
      { d: 'M 2.4 10.7 L 2 13.1', line: true },
      { d: 'M -1.5 12.8 C -1.5 15.7 -0.9 16.9 0.2 16.9 L 1.9 16.9 C 2.9 16.9 3.3 15.7 3.3 12.8 Z' },
    ],
    seams: [
      'M 1 -13.6 C -2.6 -6 -2.6 2.2 -1.4 9',
      'M 1 -13.6 C 5.6 -6 5.4 1.6 3 8.6',
      'M -7.4 -4.6 C -2 -6.6 5 -6.4 10.4 -3.8',
    ],
    glass: ['M -0.4 13.6 L 2.3 13.6 L 2.1 15.3 L -0.2 15.3 Z'],
    lights: [
      { cx: 0.9, cy: 11.8, r: 1.6, delay: 0, warm: true },
      { cx: -13.8, cy: -1.4, r: 1, delay: 1.3 },
    ],
  },

  /*
   * Patternkeepers: a sailplane, and the largest craft in the fleet because it
   * has the least on it. Pod and boom, a long wing swept aft, and a T-tail --
   * the shapes that say glider before any detail is read. The first version
   * packed all of that into the space of a fighter and became a shard.
   *
   * It carries almost nothing: no engine, no nozzle, no cargo. Which is the
   * point of the culture that flies it.
   */
  design: {
    badge: { x: -5.4, y: -5.6, size: 6 },
    parts: [
      // Pod forward, tapering to a thin tail boom.
      { d: 'M 13.4 0.6 C 11.4 -1.6 7.4 -2.8 3.4 -2.9 C -0.4 -3 -3.2 -2 -4.6 -0.4 L -15.8 0 L -15.8 1.4 L -4.4 1.9 C -2.8 3.2 0.6 3.9 4.2 3.7 C 8.6 3.4 11.8 2.4 13.4 0.6 Z' },
      // One long wing, swept aft and high: the whole aircraft is this shape.
      { d: 'M 2 -2.4 C -3.4 -4.2 -10 -6.4 -13.4 -7.6 L -12.6 -9 C -8.8 -8 -2 -5.6 3.4 -3.8 Z', alt: true },
      // T-tail. Fin off the boom, stabiliser across its top.
      { d: 'M -14.4 0.2 C -15.2 -2.4 -15.8 -4.6 -15.2 -5 C -14.2 -4.2 -13.4 -2 -12.8 0.4 Z', alt: true },
      { d: 'M -17 -5.2 L -12 -4.4 L -12.2 -3.4 L -17.2 -4.2 Z', alt: true },
    ],
    seams: [
      'M 2.6 -3.2 C -3 -5.2 -9.4 -7.2 -13 -8.3',
      'M -4.6 0.9 L 11.8 0.9',
      'M 1.2 -2.9 L 1.2 3.8',
    ],
    glass: ['M 5.4 -2.6 C 8 -2.4 10.4 -1.6 11.8 -0.4 L 5.6 -0.2 Z'],
    lights: [
      { cx: 12.2, cy: 0.4, r: 1.1, delay: 0 },
      { cx: -12.9, cy: -8.2, r: 0.9, delay: 0.8 },
      { cx: 8.2, cy: -1.1, r: 0.9, delay: 1.5, warm: true },
    ],
  },

  /*
   * Stratigraphers: an airship. Rebuilt around its fittings rather than as one
   * ellipse -- a nose cap, a cruciform tail, and a gondola slung on a pylon.
   * The parts carry the second tone, which is what stops the whole thing
   * reading as a single dark lozenge at card size.
   */
  arch: {
    badge: { x: -2, y: 0, size: 7.5 },
    parts: [
      { d: 'M -16.5 0 C -11 -6.6 5 -7 12.4 -3.2 C 15 -1.9 15.8 -1 15.8 0 C 15.8 1 15 1.9 12.4 3.2 C 5 7 -11 6.6 -16.5 0 Z' },
      // Nose cap, so the blunt end reads as a cap and not as a cut-off.
      { d: 'M 12.4 -3.2 C 15 -1.9 15.8 -1 15.8 0 C 15.8 1 15 1.9 12.4 3.2 C 11.5 1.2 11.5 -1.2 12.4 -3.2 Z', alt: true },
      // Cruciform tail: upper, lower, and one horizontal reading edge-on.
      { d: 'M -12.4 -2 C -14.6 -4.6 -16.8 -6.6 -17.6 -6 C -17.2 -4 -16.4 -1.8 -15.2 0 Z', alt: true },
      { d: 'M -12.4 2 C -14.6 4.6 -16.8 6.6 -17.6 6 C -17.2 4 -16.4 1.8 -15.2 0 Z', alt: true },
      { d: 'M -13.6 -0.9 C -16.4 -1.1 -18.9 -0.6 -18.9 0 C -18.9 0.6 -16.4 1.1 -13.6 0.9 Z', alt: true },
      // Pylon, then the car slung under it.
      { d: 'M 1 5.2 L 5.8 5.2 L 5.4 7.1 L 1.4 7.1 Z', alt: true },
      { d: 'M 0.6 6.8 C 0.8 9.9 2 11.1 3.4 11.1 C 4.8 11.1 6 9.9 6.2 6.8 Z' },
    ],
    seams: [
      'M -14 -2.5 C -6 -5.2 4 -5.2 12 -2.3',
      'M -14 2.5 C -6 5.2 4 5.2 12 2.3',
      'M -6.5 -5.8 L -6.5 5.8',
      'M 3.5 -5.2 L 3.5 5.2',
    ],
    glass: ['M 1.6 7.6 L 5.2 7.6 L 4.9 9.3 L 1.9 9.3 Z'],
    lights: [
      { cx: 2.3, cy: 8.5, r: 0.8, delay: 0, warm: true },
      { cx: 4.5, cy: 8.5, r: 0.8, delay: 0, warm: true },
      { cx: 14.4, cy: 0, r: 1.1, delay: 1.1 },
      { cx: -17.4, cy: 0, r: 0.9, delay: 1.9 },
    ],
  },

  // Forgetenders: a lifter, rising forward, its own fire slung under it.
  ai: {
    badge: { x: 3.6, y: -4.2, size: 5 },
    parts: [
      { d: 'M -8.6 -1.4 C -8 -8.2 4 -10.2 9.8 -2.8 L 7.8 4.6 C 2.4 5.6 -3.6 5.4 -6.8 4.6 Z' },
      { d: 'M -5.6 4.4 C -5.2 7.6 -4.6 9 -3.8 9 C -3.2 9 -2.8 7.6 -3 4.4 Z', alt: true },
      { d: 'M 2.4 4.6 C 2.6 7.2 3.2 8.4 3.8 8.4 C 4.4 8.4 4.8 7.2 4.6 4.6 Z', alt: true },
    ],
    seams: ['M -6.8 0.8 L 8.8 -0.6', 'M -1.4 -7.6 L -1.4 5'],
    glass: ['M 4.4 -6.4 C 6.4 -6.2 7.8 -5 8.6 -3.4 L 4.8 -3.2 Z'],
    mark: [{ d: 'M -4 -0.6 A 3 3 0 1 0 2 -0.6 A 3 3 0 1 0 -4 -0.6 Z', glow: true }],
    lights: [{ cx: 8.2, cy: -1.6, r: 1.1, delay: 0.4, warm: true }],
  },

  // Deep Current: a submersible. Sail and fin faired, hull flank left clear.
  dev: {
    badge: { x: 5.4, y: 0.2, size: 5 },
    parts: [
      { d: 'M -12.6 0 C -12.6 -5.8 -4 -6.6 3 -6.2 C 8 -5.8 10.6 -3 10.6 0 C 10.6 3 8 5.8 3 6.2 C -4 6.6 -12.6 5.8 -12.6 0 Z' },
      { d: 'M -4.4 -5.6 C -3.8 -9.6 -1.2 -10.8 1.8 -10.4 C 3.2 -10.2 3.8 -8.2 3.6 -5.2 Z', alt: true },
      { d: 'M -10.6 -2.4 C -13.4 -4.4 -15.6 -5.6 -16.2 -5 C -15.4 -2.6 -15.4 2.6 -16.2 5 C -15.6 5.6 -13.4 4.4 -10.6 2.4 Z', alt: true },
    ],
    seams: ['M -8 -3 C -2 -4.4 4 -4 8.6 -1.8', 'M -8 3 C -2 4.4 4 4 8.6 1.8'],
    glass: ['M -0.6 -9.6 C 0.8 -9.8 2 -9.2 2.4 -8 L -0.4 -7.8 Z'],
    lights: [
      { cx: -7.2, cy: 2.2, r: 1.1, delay: 0, warm: true },
      { cx: -4.6, cy: 2.4, r: 1.1, delay: 0, warm: true },
      { cx: -2, cy: 2.4, r: 1.1, delay: 0, warm: true },
      { cx: 9.4, cy: -1.4, r: 0.9, delay: 1.4 },
    ],
  },

  // Gatekeepers: an inspection drone. Arms taper out of the shell.
  test: {
    badge: { x: -1.2, y: 0.4, size: 4.6 },
    parts: [
      { d: 'M -5.8 -3.6 C -5.8 -4.6 -5.2 -5 -4.4 -5 L 3 -5 C 4.4 -5 6.6 -2.8 6.6 -1.4 L 6.6 1.8 C 6.6 3.2 4.4 5.4 3 5.4 L -4.4 5.4 C -5.2 5.4 -5.8 5 -5.8 4 Z' },
      { d: 'M -4.8 -3.8 C -7.2 -5 -9.6 -6.4 -10.8 -6.8 L -11.2 -5.6 C -9.8 -4.6 -7.2 -3 -5.2 -2.2 Z' },
      { d: 'M 2.6 -4.2 C 4.8 -5.4 7.4 -7 8.6 -7.4 L 9 -6.2 C 7.6 -5.2 5 -3.4 3.2 -2.6 Z' },
      // Seated on the arm tips. Held clear of them, the rotors read as bars
      // hanging in the air beside the drone rather than mounted to it.
      { d: 'M -14 -6.2 L -8 -6.2', line: true },
      { d: 'M 5.8 -6.8 L 11.8 -6.8', line: true },
      { d: 'M -5.6 -1.2 C -7.4 -0.6 -8.8 0 -8.8 0.6 C -8.8 1.2 -7.4 1.8 -5.6 2.2 Z', alt: true },
    ],
    seams: ['M -4 -1.8 L 3.6 -1.8', 'M -4 1.8 L 3.6 1.8'],
    glass: ['M 3.2 -1.4 C 5 -1.4 6 -0.6 6 0.4 L 3.2 0.6 Z'],
    lights: [
      { cx: 4.4, cy: -0.4, r: 1.3, delay: 0 },
      { cx: -7.6, cy: 0.6, r: 0.9, delay: 0.9, warm: true },
    ],
  },

  // Stormwrights: an orbital tug. Nozzle and hardpoint grown off the body.
  devops: {
    badge: { x: 2.6, y: 0.4, size: 5 },
    parts: [
      { d: 'M -8.4 -3.6 C -8.4 -4.2 -8 -4.4 -7.4 -4.4 L 6.2 -4 C 8 -3.6 10.4 -1.4 10.4 0 C 10.4 1.4 8 3.6 6.2 4 L -7.4 4.4 C -8 4.4 -8.4 4.2 -8.4 3.6 Z' },
      { d: 'M -7.8 -2.8 C -10.4 -2 -13.6 -0.8 -13.6 0 C -13.6 0.8 -10.4 2 -7.8 2.8 Z', alt: true },
      { d: 'M -2.6 -4.1 C -2.4 -8.8 4.2 -9 4.6 -3.9 Z', alt: true },
    ],
    seams: ['M -4.6 -4.2 L -6.4 4.3', 'M -1.4 -4.1 L -3.2 4.3', 'M 5.6 -3.6 L 5.6 3.6'],
    glass: ['M -1.6 -5.2 C -1.2 -7.6 3.2 -7.8 3.6 -5.2 Z'],
    lights: [
      { cx: 8.6, cy: 0, r: 1.1, delay: 0 },
      { cx: -12, cy: 0, r: 1.6, delay: 0.5, warm: true },
    ],
  },

  // Wardens: a patrol wedge. Mostly prow, with the wing swept out of the hull.
  sec: {
    badge: { x: 1.2, y: 0.4, size: 5 },
    parts: [
      { d: 'M -11.4 2.8 C -7.6 0.6 -1 -2.4 5 -4.6 C 8.6 -3.4 11.6 -1.4 12.8 0 C 11.6 1.4 8.6 3.4 5 4.6 C -1 4.4 -7.6 3.8 -11.4 2.8 Z' },
      { d: 'M -4.6 3.4 C -6 5.6 -7.8 7.8 -7.2 8.2 C -4.6 7.2 -0.6 5.4 1.8 4.4 Z', alt: true },
    ],
    seams: ['M -7.6 2.6 L 6.4 -2.6', 'M -3.4 4 L 3.4 1.2'],
    glass: ['M 4.6 -3 C 6.8 -2.2 8.6 -1.2 9.4 -0.4 L 5 0.6 Z'],
    lights: [
      { cx: 9.6, cy: 0.6, r: 1.5, delay: 0 },
      { cx: -2, cy: 2.6, r: 0.9, delay: 1.2, warm: true },
    ],
  },

  // Nightwatch: a scout. Dish and panel grown off the bus, lamp beneath.
  sre: {
    badge: { x: -0.8, y: 0.2, size: 4.6 },
    parts: [
      { d: 'M -5.2 -3.6 C -5.2 -4.4 -4.8 -4.8 -4.2 -4.8 L 2.4 -4.8 C 3.2 -4.8 3.6 -4.4 3.6 -3.6 L 3.6 3.6 C 3.6 4.4 3.2 4.8 2.4 4.8 L -4.2 4.8 C -4.8 4.8 -5.2 4.4 -5.2 3.6 Z' },
      { d: 'M 3 -5.4 C 6.2 -7.2 9.8 -7.6 9.8 0 C 9.8 7.6 6.2 7.2 3 5.4 Z', alt: true },
      { d: 'M -4.6 -2.2 C -8.2 -3.6 -12.8 -5.4 -13.4 -5 L -13.4 0.6 C -12.8 1 -8.2 0.2 -4.6 0.8 Z', alt: true },
    ],
    seams: [
      'M 5.4 -5.4 L 5.4 5.4',
      'M 7.4 -6 L 7.4 6',
      'M -7.6 -3.2 L -7.6 0.4',
      'M -10.6 -4.4 L -10.6 0.2',
    ],
    glass: ['M -3.6 -3.6 L 1.8 -3.6 L 1.8 -1.6 L -3.6 -1.6 Z'],
    mark: [{ d: 'M -2.6 5.6 A 1.8 1.8 0 1 0 1 5.6 A 1.8 1.8 0 1 0 -2.6 5.6 Z', glow: true }],
    lights: [{ cx: -0.8, cy: -5.8, r: 1.1, delay: 0 }],
  },
};

/*
 * A sky per culture, not one sky nine times.
 *
 * The first pass used a single hard-coded list of star positions, which made
 * every night portrait identical behind a different planet. Seeding from the
 * culture id gives each world its own sky and keeps it stable across renders,
 * the same trick NightSky uses for the page backdrop.
 */
const seeded = (seed) => {
  let value = seed;
  return () => {
    value = (value * 1664525 + 1013904223) % 4294967296;
    return value / 4294967296;
  };
};

const seedFrom = (id) => [...id].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 100000, 7) + 1;

const starfield = (id) => {
  const random = seeded(seedFrom(id));
  return Array.from({ length: 16 }, () => ({
    cx: random() * VB_W,
    cy: 6 + random() * 74,
    r: 0.55 + random() * 0.95,
  }));
};

/*
 * How a culture lights its own dark side. Each returns points in ground space;
 * only night cultures ever draw them, which is what makes the settlement a
 * night-culture signature rather than a decoration everyone shares.
 */
const SETTLEMENT = {
  beacons: () => [12, 44, 86, 132, 164].map((x, i) => ({ x, y: 2 + (i % 2) * 3, r: 1.5 })),
  keeps: () => [22, 27, 32, 96, 101, 150].map((x, i) => ({ x, y: 2 + (i % 3), r: 1.3 })),
  circuits: () => Array.from({ length: 11 }, (_, i) => ({ x: 8 + i * 16, y: 3, r: 1.1 })),
  waypoints: () => [16, 56, 96, 136, 168].map((x) => ({ x, y: 4, r: 1.6 })),
  shoals: () =>
    [10, 14, 19, 24, 62, 67, 71, 76, 81, 118, 123, 128, 133, 158, 163].map((x, i) => ({
      x,
      y: 1 + (i % 4) * 2,
      r: 1.1,
    })),
  vents: () => [30, 70, 110, 150].map((x, i) => ({ x, y: 2 + (i % 2) * 4, r: 2.2 })),
  cuts: () => Array.from({ length: 8 }, (_, i) => ({ x: 14 + i * 22, y: 2 + (i % 2) * 4, r: 1.2 })),
  veins: () => Array.from({ length: 13 }, (_, i) => ({ x: 6 + i * 13, y: 2 + (i % 3) * 2, r: 1 })),
  waystations: () => [26, 74, 122, 160].map((x) => ({ x, y: 3, r: 1.8 })),
};

const CitizenPortrait = ({ hat, className = '', wide = false }) => {
  const uid = useId().replace(/:/g, '');
  const night = hat.diurnal < 0.5;
  const accent = `var(--tmc-hat-${hat.id})`;
  const body = hat.body ?? { glow: 0, rings: 0, moons: [] };
  const lights = night ? (SETTLEMENT[hat.settlement] ?? (() => []))() : [];
  const stars = night ? starfield(hat.id) : [];
  const form = CITIZEN_FORM[hat.id] ?? CITIZEN_FORM.arch;
  const craft = CRAFT[hat.id];
  const sceneWidth = wide ? WIDE_VB_W : VB_W;
  const sceneOffset = wide ? (WIDE_VB_W - VB_W) / 2 : 0;

  // The world overhead: the same body the orrery draws, at card scale.
  const planet = { cx: 137, cy: 32, r: hat.id === 'ai' ? 11 : 15 };

  return (
    <svg
      className={`citizen ${night ? 'is-night' : 'is-day'} ${className}`}
      viewBox={`0 0 ${sceneWidth} ${VB_H}`}
      /*
       * Slice, not meet: the wide (boundary) card fixes a height in rem while
       * its width tracks the grid column, so its box ratio rarely matches this
       * viewBox's 4:1. "meet" would letterbox that mismatch, leaving a gap
       * between the ground and the card body below it; "slice" fills the box
       * and crops the overflow instead, so the ground always meets the edge.
       */
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={`A citizen of ${hat.culture}, on the ${hat.name} world`}
      style={{
        '--accent': accent,
        '--glow': body.glow,
        '--cz-ink': form.ink,
        '--cz-ink-alt': form.inkAlt ?? form.ink,
        '--cz-mark-ink': form.markInk ?? accent,
      }}
    >
      <defs>
        <linearGradient id={`sky-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="cz-sky-top" />
          <stop offset="100%" className="cz-sky-base" />
        </linearGradient>
        <SphereDefs uid={uid} />
        <SurfaceDefs uid={uid} />
        <clipPath id={`world-${uid}`}>
          <circle cx={planet.cx} cy={planet.cy} r={planet.r} />
        </clipPath>
        {/*
          * Square. The portrait runs flush to the top of its card, and the
          * card already rounds and clips -- rounding here too put a second
          * curve inside the first at the top, and a rounded corner floating
          * mid-card at the bottom, where the body carries straight on.
          */}
        <clipPath id={`frame-${uid}`}>
          <rect x="0" y="0" width={sceneWidth} height={VB_H} />
        </clipPath>
      </defs>

      <g clipPath={`url(#frame-${uid})`}>
        <rect width={sceneWidth} height={VB_H} fill={`url(#sky-${uid})`} />

        {wide && (
          <path
            className="cz-ground"
            d={`M 0 ${GROUND + 6} Q 104 ${GROUND - 5} 208 ${GROUND + 2} T ${sceneWidth} ${GROUND - 2} L ${sceneWidth} ${VB_H} L 0 ${VB_H} Z`}
          />
        )}

        <g transform={wide ? `translate(${sceneOffset} 0)` : undefined}>

        {/* Night sky gets its own stars; day gets nothing, which is the point. */}
        {stars.map((star, i) => (
          <circle key={i} className="cz-star" cx={star.cx} cy={star.cy} r={star.r} />
        ))}

        {/* The world, with whatever the canon says it carries. */}
        {/*
          * The same body the orrery draws, at card scale: its own procedural
          * terrain under the shared glass treatment. A flat disc tinted by
          * accent made nine worlds look like one world in nine colours, which
          * is precisely what HAT_SURFACE and HAT_TILT exist to prevent.
          */}
        {body.glow > 0 && (
          <circle className="cz-glow" cx={planet.cx} cy={planet.cy} r={planet.r * 2.1} fill={accent} />
        )}
        <SphereAtmosphere uid={uid} cx={planet.cx} cy={planet.cy} r={planet.r} color={accent} />
        {body.rings > 0 &&
          Array.from({ length: body.rings }, (_, i) => (
            <ellipse
              key={i}
              className="cz-ring"
              cx={planet.cx}
              cy={planet.cy}
              rx={planet.r * (1.45 + i * 0.22)}
              ry={planet.r * (0.4 + i * 0.06)}
              transform={`rotate(-16 ${planet.cx} ${planet.cy})`}
            />
          ))}
        <g clipPath={`url(#world-${uid})`}>
          <PlanetSurface
            uid={uid}
            cx={planet.cx}
            cy={planet.cy}
            r={planet.r}
            color={accent}
            surface={HAT_SURFACE[hat.id]}
            tilt={HAT_TILT[hat.id] ?? 0}
          />
        </g>
        <SphereGlass uid={uid} cx={planet.cx} cy={planet.cy} r={planet.r} />
        {body.moons.map((moon, i) => {
          const angle = (-52 + i * 47) * (Math.PI / 180);
          return (
            <circle
              key={moon}
              className="cz-moon"
              cx={planet.cx + Math.cos(angle) * planet.r * 1.9}
              cy={planet.cy + Math.sin(angle) * planet.r * 1.05}
              r={1.9}
            />
          );
        })}
        {Array.from({ length: body.satellites ?? 0 }, (_, i) => {
          const angle = (-72 + i * 40) * (Math.PI / 180);
          const radius = planet.r * (2.05 + (i % 3) * 0.16);
          return (
            <circle
              key={`satellite-${i}`}
              className="cz-satellite"
              cx={planet.cx + Math.cos(angle) * radius}
              cy={planet.cy + Math.sin(angle) * radius * 0.62}
              r={0.9 + (i % 2) * 0.35}
              style={{ animationDelay: `${i * -0.7}s` }}
            />
          );
        })}

        {/* Ground. */}
        <path
          className="cz-ground"
          d={`M 0 ${GROUND + 6} Q 52 ${GROUND - 5} 104 ${GROUND + 2} T ${VB_W} ${GROUND - 2} L ${VB_W} ${VB_H} L 0 ${VB_H} Z`}
        />
        <g transform={`translate(0 ${GROUND + 12})`}>
          {lights.map((light, i) => (
            <circle key={i} className="cz-light" cx={light.x} cy={light.y} r={light.r} />
          ))}
        </g>

        {/* The citizen, and the standard carrying their mark. */}
        <g
          className="cz-standard"
          transform={`translate(${FIG_X} ${GROUND + 4}) scale(${FIG_SCALE}) translate(13 0)`}
        >
          <path className="cz-pole" d="M 0 2 L 0 -48" />
          {body.glow > 0 && (
            <circle className="cz-standard-glow" cx="15" cy="-36" r="18" fill={accent} />
          )}
          {/* Flying from the pole, with a slack curve top and bottom. */}
          <path
            className="cz-flag"
            fill={accent}
            d="M 0 -46 C 9 -49.5 20 -43 30 -46.5 L 30 -26.5 C 20 -23 9 -29.5 0 -26 Z"
          />
          <g transform="translate(15 -36)">
            <HatGlyph hatId={hat.id} size={15} halo />
          </g>
        </g>

        {/*
          * Three on the left, reading outward from the ground: the flag planted,
          * the species beside it, and the culture's craft aloft above them both.
          * The planet keeps the right, so the two halves never collide.
          */}
        {craft && (
          <g className="cz-craft">
            {craft.parts.map((part, i) => (
              <path
                key={i}
                className={`${part.line ? 'cz-limb' : ''}${part.alt ? ' cz-alt' : ''}`.trim() || undefined}
                d={part.d}
              />
            ))}
            {(craft.glass ?? []).map((pane, i) => (
              <path key={`cg${i}`} className="cz-glass" d={pane} />
            ))}
            {(craft.seams ?? []).map((seam, i) => (
              <path key={`cs${i}`} className="cz-seam" d={seam} />
            ))}
            {(craft.mark ?? []).map((detail, i) => (
              <path
                key={`cm${i}`}
                className={`cz-mark${detail.glow ? ' is-lit' : ''}${detail.line ? ' is-line' : ''}`}
                d={detail.d}
              />
            ))}
            {craft.badge && (
              <g className="cz-livery" transform={`translate(${craft.badge.x} ${craft.badge.y})`}>
                <HatGlyph hatId={hat.id} size={craft.badge.size} />
              </g>
            )}
            {(craft.lights ?? []).map((light, i) => (
              <circle
                key={`cl${i}`}
                className={`cz-blink${light.warm ? ' is-warm' : ''}`}
                cx={light.cx}
                cy={light.cy}
                r={light.r}
                style={{ animationDelay: `${light.delay}s` }}
              />
            ))}
          </g>
        )}

        {form.hover && (
          <ellipse className="cz-shadow" cx={FIG_X} cy={GROUND + 6} rx="8.5" ry="2.6" />
        )}
        <g
          className="cz-figure"
          transform={`translate(${FIG_X} ${GROUND + 4}) scale(${FIG_SCALE}) translate(0 ${form.hover ? -7 : 0})`}
        >
          {form.parts.map((part, i) => (
            <path
              key={i}
              className={`${part.line ? 'cz-limb' : ''}${part.alt ? ' cz-alt' : ''}`.trim() || undefined}
              d={part.d}
              transform={part.spin ? `rotate(${part.spin} 0 -26)` : undefined}
            />
          ))}
          {form.reach && <path className="cz-limb" d={form.reach} />}
          {(form.mark ?? []).map((detail, i) => (
            <path
              key={`m${i}`}
              className={`cz-mark${detail.glow ? ' is-lit' : ''}${detail.line ? ' is-line' : ''}`}
              d={detail.d}
            />
          ))}
        </g>
        </g>
      </g>
    </svg>
  );
};

export default CitizenPortrait;
